import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, message, Spin } from "antd";
import {
    CheckCircleTwoTone,
    CloseCircleTwoTone,
    ClockCircleTwoTone,
    DollarCircleTwoTone
} from '@ant-design/icons';
import _commonService from '../../../modules/commonService';
import _capitalProviderService from '../../../modules/capitalProviderService';
import SidebarLayoutConsumer from '../Administrator/SidebarLayoutConsumer';
import { COMMON_ERROR_MSG } from '../../../constants/constants';
import moment from "moment";
import ApplicationListConsumerPortal from '../Administrator/ApplicationListConsumerPortal';
import { useLocation } from "react-router-dom";

import '../../../assets/dist/ConsumerProject/CapitalProvider/CapitalProvider.css';

const { Title, Text } = Typography;

const CapitalProviderDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [bankerMonthlySummaryData, setBankerMonthlySummaryData] = useState([]);
    const FirstName = localStorage.getItem("capitalProviderFirstName");
    const [loanOfficerName] = useState(FirstName);
    const capitalProviderId = localStorage.getItem("capitalProviderUserId");
    const capitalProviderID = capitalProviderId ? parseInt(capitalProviderId) : null;
    const [loanApplications, setLoanApplications] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [sortBy, setSortBy] = useState(null);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [debouncedSearchText, setDebouncedSearchText] = useState("");
    const [applicationStatus, setApplicationStatus] = useState();

    const location = useLocation();
    let data = location.state;
    let appStatusFlag = 0;
    if (data && data.applicationStatusFlag) {
        appStatusFlag = data.applicationStatusFlag;
    }

    const [applicationStatusFilter] = useState(appStatusFlag);

    useEffect(() => {
        setApplicationStatus(applicationStatusFilter);
    }, [applicationStatusFilter]);

    useEffect(() => {
        getAllLoanApplicationListByLoanOfficerId();
    }, [debouncedSearchText, applicationStatus, toDate, sortBy, pageIndex, pageSize]);


    useEffect(() => {
        if (debouncedSearchText !== "") {
            setPageIndex(1);
            setPageSize(10);
        }
    }, [debouncedSearchText]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchText(searchTerm && searchTerm.trim());
        }, 900);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    const getAllLoanApplicationListByLoanOfficerId = async () => {
        if ((applicationStatus != undefined || applicationStatus == 0)) {
            const params = {
                loanOfficerId: capitalProviderID,
                ByAppStatus: applicationStatus,
                startDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : "",
                endDate: toDate ? moment(toDate).format("YYYY-MM-DD") : "",
                pageIndex,
                pageSize,
                searchTerm: debouncedSearchText,
                sortBy,
                sortAsc: true,
            };
            setLoading(true);
            try {
                const resObj = await _capitalProviderService.getAllLoanApplicationListByLoanOfficerId(params);

                if (
                    resObj?.data?.IsSuccess &&
                    resObj.data.Obj &&
                    Array.isArray(resObj.data.Obj.List)
                ) {
                    setLoanApplications(resObj.data.Obj.List);
                    setTotalCount(resObj.data.Obj.TotalRecords || 0);
                    setLoading(false);
                } else {
                    message.error(resObj.data.Message || COMMON_ERROR_MSG)
                    setLoading(false);
                }
            } catch (error) {
                console.error("Error fetching loan applications:", error);
                setLoading(false);
            }
        }
    };

    const handleClearFilters = () => {
        setSearchTerm("");
        setFromDate(null);
        setToDate(null);
        setSortBy(null);
        setApplicationStatus(0);
        setPageIndex(1);
        setPageSize(10);
    };

    const handlePaginationChange = (page, size) => {
        setPageIndex(page);
        setPageSize(size);
    };

    useEffect(() => {
        getBankerDataById()
    }, []);

    const getBankerDataById = async () => {
        try {
            setLoading(true);
            const res = await _capitalProviderService.getLoanOfficerDashboardSummary(capitalProviderID);
            if (res.data.IsSuccess) {
                setBankerMonthlySummaryData(res?.data?.Obj.monthlySummary);
            } else {
                message.error(res.data.Message || COMMON_ERROR_MSG);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const currentMonthYear = _commonService.getCurrentMonthAndYear();

    return (
        <Spin spinning={loading}>
            <SidebarLayoutConsumer>
                <Row gutter={[0, 32]} className='capital-provider-dashboard' >
                    <Col span={24}>
                        <Title level={3}>Hello, {loanOfficerName}!</Title>
                        <Title level={5}>Monthly Summary, {currentMonthYear}</Title>
                    </Col>
                    <Col span={24}>
                        <Row gutter={[24, 16]} className='banker-dashboard'>
                            <Col xs={24} sm={12} md={12} lg={6}>
                                <Card className='cardViewMain'>
                                    <div className='title-icon'>
                                        <Text>Funded Applications</Text>
                                        <img className="icon" src={`${process.env.PUBLIC_URL}/tickmark-colered.svg`} alt="Building" />
                                    </div>
                                    <div className='text-data'>
                                        <Title level={3}>
                                            {bankerMonthlySummaryData && bankerMonthlySummaryData.LoanApproved || 0}
                                        </Title>
                                        <Text>Capital disbursed successfully</Text>
                                    </div>
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card className='cardViewMain'>
                                    <div className='title-icon'>
                                        <Text>Unreviewed Applications</Text>
                                        <img className="icon" src={`${process.env.PUBLIC_URL}/doc-colored.svg`} alt="Building" />
                                    </div>
                                    <div className='text-data'>
                                        <Title level={3}>
                                            {bankerMonthlySummaryData && bankerMonthlySummaryData.UnreviewedApplication || 0}
                                        </Title>
                                        <Text>Submitted but not assessed</Text>
                                    </div>
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card className='cardViewMain'>
                                    <div className='title-icon'>
                                        <Text>In-Progress Applications</Text>
                                        <img className="icon" src={`${process.env.PUBLIC_URL}/clock-colored.svg`} alt="Building" />
                                    </div>
                                    <div className='text-data'>
                                        <Title level={3}>
                                            {bankerMonthlySummaryData && bankerMonthlySummaryData.InProgressApplication || 0}
                                        </Title>
                                        <Text>Under review by you</Text>
                                    </div>
                                </Card>
                            </Col>

                            <Col xs={24} sm={12} md={6}>
                                <Card className='cardViewMain'>
                                    <div className='title-icon'>
                                        <Text>Issued Amount</Text>
                                        <img className="icon" src={`${process.env.PUBLIC_URL}/dollar-colered.svg`} alt="Building" />
                                    </div>
                                    <div className='text-data'>
                                        <Title level={3}>
                                            $ {bankerMonthlySummaryData && _commonService.formatNumberWithCommas(bankerMonthlySummaryData.IssuedAmount) || 0}
                                        </Title>
                                        <Text>Total capital issued</Text>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={24}>
                        <div style={{ marginBottom: 24 }}>
                            <ApplicationListConsumerPortal
                                allApplications={loanApplications}
                                loading={loading}
                                pageIndex={pageIndex}
                                pageSize={pageSize}
                                totalCount={totalCount}
                                onPaginationChange={handlePaginationChange}
                                searchTerm={searchTerm}
                                fromDate={fromDate}
                                toDate={toDate}
                                sortBy={sortBy}
                                onSearchChange={setSearchTerm}
                                fromDateChange={setFromDate}
                                toDateChange={setToDate}
                                onSortChange={setSortBy}
                                onClearFilters={handleClearFilters}
                                // onApplicationStatusChange={setApplicationStatus}
                                onApplicationStatusChange={(status) => {
                                    setApplicationStatus(status);
                                    setPageIndex(1);
                                    setPageSize(10);
                                }}
                                applicationStatusForFilter={applicationStatus}
                                isFromLoanOfficerDashboard={true}
                            />
                        </div>
                    </Col>
                </Row>
            </SidebarLayoutConsumer>
        </Spin>
    );
};

export default CapitalProviderDashboard;